import { createSelector } from 'reselect'
import { get, groupBy, reject, maxBy, minBy } from 'lodash';
import moment from 'moment'
import { ethers } from 'ethers';

const GREEN = '#2ECC71'
const RED = '#E84142'


const account = state => get(state, 'provider.account')
const tokens = state => get(state, 'tokens.contracts')
const decimals = state => get(state, 'tokens.decimals', [18, 18])
const events = state =>get(state, 'exchange.events')
const chartTimeframe = state => get(state, 'exchange.chartTimeframe', '1m')
const allOrders = state => get(state, 'exchange.allOrders.data', [])
const cancelledOrders = state => get(state, 'exchange.cancelledOrders.data', [])
const filledOrders = state => get(state, 'exchange.filledOrders.data', [])

const openOrders = state => {
  const all = allOrders(state)
  const filled = filledOrders(state)
  const cancelled = cancelledOrders(state)

  const openOrders = reject(all, (order) => {
    const orderFilled = filled.some((o) => o.id.toString() === order.id.toString())
    const orderCancelled = cancelled.some((o) => o.id.toString() === order.id.toString())
    return(orderFilled || orderCancelled)
  })

  return openOrders

}
export const myEventSelector = createSelector(
  account,
  events,
  (account, events) => {

    events = events.filter((e) => e.args.user === account)
    return events
  }
  )
// ------------------------------------------------------------------------------
// MY OPEN ORDERS

export const myOpenOrdersSelector = createSelector(
    account,
    tokens,
    openOrders,
    decimals,
    (account, tokens, orders, decimals) => {
      if (!tokens[0] || !tokens[1]) { return }

      // Filter orders created by current account
      orders = orders.filter((o) => o.user === account)

      // Filter orders by token addresses
      orders = orders.filter((o) => o.tokenGet === tokens[0].address || o.tokenGet === tokens[1].address)
      orders = orders.filter((o) => o.tokenGive === tokens[0].address || o.tokenGive === tokens[1].address)

      // Decorate orders - add display attributes
      orders = decorateMyOpenOrders(orders, tokens, decimals)

      // Sort orders by date descending
      orders = orders.sort((a, b) => b.timestamp - a.timestamp)

      return orders
  }
)

const decorateMyOpenOrders = (orders, tokens, decimals) => {
  return(
    orders.map((order) => {
      order = decorateOrder(order, tokens, decimals)
      order = decorateMyOpenOrder(order, tokens)
      return(order)
    })
  )
}

const decorateMyOpenOrder = (order, tokens) => {
  let orderType = order.tokenGive === tokens[1].address ? 'buy' : 'sell'

  return({
    ...order,
    orderType,
    orderTypeClass: (orderType === 'buy' ? GREEN : RED)
  })
}

const decorateOrder = (order, tokens, decimals) => {
  let token0Amount, token1Amount

  // token0 = MG (base), token1 = USDT (quote)
  // price = quote / base (e.g. 0.164 USDT per MG)
  if (order.tokenGive === tokens[1].address) {
    // Buy order: giving quote (USDT), getting base (MG)
    token0Amount = order.amountGet   // MG amount
    token1Amount = order.amountGive  // USDT amount
  } else {
    // Sell order: giving base (MG), getting quote (USDT)
    token0Amount = order.amountGive  // MG amount
    token1Amount = order.amountGet   // USDT amount
  }

  // Calculate token price to 5 decimal places
  const precision = 100000
  let tokenPrice = (token1Amount / token0Amount)
  tokenPrice = Math.round(tokenPrice * precision) / precision

  const dec0 = decimals && decimals[0] ? decimals[0] : 18
  const dec1 = decimals && decimals[1] ? decimals[1] : 18

  return ({
    ...order,
    token1Amount: ethers.utils.formatUnits(token1Amount, dec1),
    token0Amount: ethers.utils.formatUnits(token0Amount, dec0),
    tokenPrice,
    formattedTimestamp: moment.unix(order.timestamp).format('h:mm:ssa d MMM D')
  })
}


// ------------------------------------------------------------------------------
// ALL FILLED ORDERS

export const filledOrdersSelector = createSelector(
  filledOrders,
  tokens,
  decimals,
  (orders, tokens, decimals) => {
    if (!tokens[0] || !tokens[1]) { return }

    // Filter orders by selected tokens
    orders = orders.filter((o) => o.tokenGet === tokens[0].address || o.tokenGet === tokens[1].address)
    orders = orders.filter((o) => o.tokenGive === tokens[0].address || o.tokenGive === tokens[1].address)

    // Sort orders by time ascending for price comparison
    orders = orders.sort((a, b) => a.timestamp - b.timestamp)

    // Decorate the orders
    orders = decorateFilledOrders(orders, tokens, decimals)

    // Sort orders by date descending for display
    orders = orders.sort((a, b) => b.timestamp - a.timestamp)

    return orders

  }
)

const decorateFilledOrders = (orders, tokens, decimals) => {
  // Track previous order to compare history
  let previousOrder = orders[0]

  return(
    orders.map((order) => {
      // decorate each individual order
      order = decorateOrder(order, tokens, decimals)
      order = decorateFilledOrder(order, previousOrder)
      previousOrder = order  // Update the previous order once it's decorated
      return order
    })
  )
}

const decorateFilledOrder = (order, previousOrder) => {
  return({
    ...order,
    tokenPriceClass: tokenPriceClass(order.tokenPrice, order.id, previousOrder)
  })
}

const tokenPriceClass = (tokenPrice, orderId, previousOrder) => {
  // Show green price if only one order exists
  if (previousOrder.id === orderId) {
    return GREEN
  }

  // Show green price if order price higher than previous order
  // Show red price if order price lower than previous order
  if (previousOrder.tokenPrice <= tokenPrice) {
    return GREEN // success
  } else {
    return RED // danger
  }
}

// ------------------------------------------------------------------------------
// ORDER BOOK

export const orderBookSelector = createSelector(
  openOrders,
  tokens,
  decimals,
  (orders, tokens, decimals) => {
    if (!tokens[0] || !tokens[1]) { return }

    // Filter orders by selected tokens
    orders = orders.filter((o) => o.tokenGet === tokens[0].address || o.tokenGet === tokens[1].address)
    orders = orders.filter((o) => o.tokenGive === tokens[0].address || o.tokenGive === tokens[1].address)

    // Decorate orders
    orders = decorateOrderBookOrders(orders, tokens, decimals)

    // Group orders by "orderType"
    orders = groupBy(orders, 'orderType')

    // Fetch buy orders
    const buyOrders = get(orders, 'buy', [])

    // Sort buy orders by token price
     orders = {
        ...orders,
        buyOrders: buyOrders.sort((a, b) => b.tokenPrice - a.tokenPrice)
      }

    // Fetch sell orders
    const sellOrders = get(orders, 'sell', [])

    // Sort sell orders by token price
    orders = {
      ...orders,
      sellOrders: sellOrders.sort((a, b) => b.tokenPrice - a.tokenPrice)
    }

    return orders
  }
)

const decorateOrderBookOrders = (orders, tokens, decimals) => {
  return(
    orders.map((order) => {
      order = decorateOrder(order, tokens, decimals)
      order = decorateOrderBookOrder(order, tokens)
      return(order)
    })
  )
}

const decorateOrderBookOrder = (order, tokens) => {
  const orderType = order.tokenGive === tokens[1].address ? 'buy' : 'sell'

  return({
    ...order,
    orderType,
    orderTypeClass: (orderType === 'buy' ? GREEN : RED),
    orderFillAction: (orderType === 'buy' ? 'sell' : 'buy')
  })
}
//------------------------------------------------------------------------------
//MY FILLED ORDERS
export const myFilledOrdersSelector = createSelector(
    account,
    tokens,
    filledOrders,
    decimals,
    (account, tokens, orders, decimals) => {
      if (!tokens[0] || !tokens[1]) { return }

      //Find our orders
      orders = orders.filter((o) => o.user === account || o.creator === account)
      // Filter orders for current trading pair
      orders = orders.filter((o) => o.tokenGet === tokens[0].address || o.tokenGet === tokens[1].address)
      orders = orders.filter((o) => o.tokenGive === tokens[0].address || o.tokenGive === tokens[1].address)

      //Sort by date ascending
      orders = orders.sort((a, b) => b.timestamp - a.timestamp)
      //Decorate orders - add display attributes
      orders = decorateMyFilledOrders(orders, account, tokens, decimals)

      return orders
  }
)

const decorateMyFilledOrders = (orders, account, tokens, decimals) => {
  return(
    orders.map((order) => {
      order = decorateOrder(order, tokens, decimals)
      order = decorateMyFilledOrder(order, account, tokens)
      return(order)
    })
  )
}

const decorateMyFilledOrder = (order, account, tokens) => {
  const myOrder = order.creator === account
  let orderType
  if(myOrder) {
    orderType = order.tokenGive === tokens[1].address ? 'buy' : 'sell'
  } else {
    orderType = order.tokenGive === tokens[1].address ? 'sell' : 'buy'
  }



  return({
    ...order,
    orderType,
    orderClass: (orderType === 'buy' ? GREEN : RED),
    orderSign: (orderType === 'buy' ? '+' : '-')
  })
}
// ------------------------------------------------------------------------------
// PRICE CHART

export const priceChartSelector = createSelector(
  filledOrders,
  tokens,
  decimals,
  chartTimeframe,
  (orders, tokens, decimals, timeframe) => {
    if (!tokens[0] || !tokens[1]) { return }

    // Filter orders by selected tokens
    orders = orders.filter((o) => o.tokenGet === tokens[0].address || o.tokenGet === tokens[1].address)
    orders = orders.filter((o) => o.tokenGive === tokens[0].address || o.tokenGive === tokens[1].address)

    // Sort orders by date ascending to compare history
    orders = orders.sort((a, b) => a.timestamp - b.timestamp)

    // Decorate orders - add display attributes
    orders = orders.map((o) => decorateOrder(o, tokens, decimals))

    // Get last 2 order for final price & price change
    let secondLastOrder, lastOrder
    [secondLastOrder, lastOrder] = orders.slice(orders.length - 2, orders.length)

    // get last order price
    const lastPrice = get(lastOrder, 'tokenPrice', 0)

    // get second last order price
    const secondLastPrice = get(secondLastOrder, 'tokenPrice', 0)

    return ({
      lastPrice,
      lastPriceChange: (lastPrice >= secondLastPrice ? '+' : '-'),
      series: [{
        data: buildGraphData(orders, timeframe)
      }]
    })

  }
)

const groupKeyForTimeframe = (timestamp, timeframe) => {
  switch (timeframe) {
    case '1m':
      return moment.unix(timestamp).startOf('minute').format()
    case '5m':
      return moment.unix(Math.floor(timestamp / 300) * 300).format()
    case '1D':
      return moment.unix(timestamp).startOf('day').format()
    case '1h':
    default:
      return moment.unix(timestamp).startOf('hour').format()
  }
}

const buildGraphData = (orders, timeframe) => {
  // Group the orders by the selected timeframe
  orders = groupBy(orders, (o) => groupKeyForTimeframe(o.timestamp, timeframe))

  // Get each period where data exists
  const periods = Object.keys(orders)

  // Build the graph series
  const graphData = periods.map((period) => {
    // Fetch all orders from current period
    const group = orders[period]

    // Calculate price values: open, high, low, close
    const open = group[0] // first order
    const high = maxBy(group, 'tokenPrice') // high price
    const low = minBy(group, 'tokenPrice') // low price
    const close = group[group.length - 1] // last order

    return({
      x: new Date(period),
      y: [open.tokenPrice, high.tokenPrice, low.tokenPrice, close.tokenPrice]
    })
  })

  return graphData
}
